/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import Spinner from '@strava/ui/Spinner';
import cx from '@strava/ui/clsx';
import createNetworkingClient from 'utils/networking-client';
import I18n from 'utils/I18n';
import styles from './SuggestedActivity.scss';
import EntryHeader from '../shared/media/EntryHeader';
import Activity from '../Activity';
import Card from '../shared/media/Card';

// namespaced translations macro
const t = (keyPath) => I18n.t(`components.feed.discovery.opt_out.${keyPath}`);

// vs = visibility status
const vs = {
  VISIBLE: 'VISIBLE',
  HIDDEN: 'HIDDEN',
  HIDDEN_WITH_FEEDBACK_GIVEN: 'HIDDEN_WITH_FEEDBACK_GIVEN'
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const mockOptOut = window.location.search.includes('mock-opt-out');

const optOutApiCall = (activityId) =>
  mockOptOut
    ? sleep(1000)
    : createNetworkingClient().post(
        `/discovery/activities/${activityId}/opt_out`
      );

const feedbackApiCall = (activityId, reason) =>
  mockOptOut
    ? sleep(800)
    : createNetworkingClient().post(
        // TODO FIXME - real API endpoint for this functionality is TBD
        `/discovery/activities/${activityId}/opt_out_reason`,
        { reason }
      );

const undoApiCall = (activityId) =>
  mockOptOut
    ? sleep(800)
    : createNetworkingClient().post(
        // TODO FIXME - real API endpoint for this functionality is TBD
        `/discovery/activities/${activityId}/undo_opt_out`
      );

// Quick and dirty state management & error handling for api calls. Basically a
// minimal implementation of react-query with state tracking only.
//
// status can be 'idle', 'loading', 'error', or 'success'
const useMutation = (apiFn) => {
  const [status, setStatus] = useState('idle');
  const mutate = (...params) => {
    setStatus('loading');
    const unchainedPromise = apiFn(...params);
    unchainedPromise
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
    return unchainedPromise;
  };
  return { status, mutate };
};

const SuggestedActivity = (props) => {
  const { activity } = props;

  const [hideState, setHideState] = useState(vs.VISIBLE);
  const optOutAction = useMutation(optOutApiCall);
  const feedbackAction = useMutation(feedbackApiCall);
  const undoAction = useMutation(undoApiCall);
  const anythingIsLoading =
    optOutAction.status === 'loading' ||
    feedbackAction.status === 'loading' ||
    undoAction.status === 'loading';

  const onClickHideActivity = async () => {
    await optOutAction.mutate(activity.id);
    setHideState(vs.HIDDEN);
  };

  const onFeedback = async (reason) => {
    await feedbackAction.mutate(activity.id, reason);
    setHideState(vs.HIDDEN_WITH_FEEDBACK_GIVEN);
  };

  const unHideActivity = async () => {
    await undoAction.mutate(activity.id);
    setHideState(vs.VISIBLE);
  };

  return (
    <>
      {/* Context header is always shown regardless of activity visibility state */}
      {activity.activityContextHeader && (
        <EntryHeader className={styles.contextHeader}>
          {activity.activityContextHeader.subtitle}
        </EntryHeader>
      )}
      <LoadingOverlay isLoading={anythingIsLoading}>
        {(() => {
          switch (hideState) {
            case vs.HIDDEN:
              return (
                <ActivityHidden
                  onFeedback={onFeedback}
                  onUndo={unHideActivity}
                />
              );
            case vs.HIDDEN_WITH_FEEDBACK_GIVEN:
              return <ActivityHiddenWithFeedback onUndo={unHideActivity} />;
            default:
              return (
                <Activity
                  extraEntityActions={[
                    [t('hide_this_activity'), { onSelect: onClickHideActivity }]
                  ]}
                  {...props}
                  entity="Activity"
                />
              );
          }
        })()}
      </LoadingOverlay>
    </>
  );
};

const LoadingOverlay = ({ children, isLoading }) => (
  <div className={cx(styles.loadingOverlay, isLoading && styles.isLoading)}>
    {children}
    {isLoading && (
      <div className={styles.loadingSpinner}>
        <Spinner />
      </div>
    )}
  </div>
);

const ActivityHidden = ({ onFeedback, onUndo }) => (
  <Card className={styles.activityHidden}>
    <h1>{t('activity_hidden')}</h1>
    <div className={styles.subhead}>{t('hide_reason_question')}</div>
    <div className={styles.hiddenActivityActions}>
      <button onClick={() => onFeedback('NOT_INTERESTED_SPORT')}>
        {t('not_interested_sport_type')}
      </button>
      <button onClick={() => onFeedback('NOT_INTERESTED_ATHLETE')}>
        {t('not_interested_athlete')}
      </button>
      <button onClick={() => onFeedback('NOT_USEFUL_OR_MOTIVATING')}>
        {t('not_useful_or_motivating')}
      </button>
      <button onClick={() => onFeedback('NONE_OF_THE_ABOVE')}>
        {t('none_of_the_above')}
      </button>
      <button onClick={onUndo}>{t('undo_opt_out')}</button>
    </div>
  </Card>
);

const ActivityHiddenWithFeedback = ({ onUndo }) => (
  <Card className={cx(styles.activityHidden, styles.withFeedback)}>
    <h1>{t('activity_hidden')}</h1>
    <button onClick={onUndo}>{t('undo_opt_out')}</button>
  </Card>
);

export default SuggestedActivity;
